import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { TextAreaFieldComponent } from 'src/app/components/form/textarea-field/textarea-field.component';
import { ProjectFinanceService } from 'src/app/services/api/project-finance.service';
import { AccessControlService } from 'src/app/services/access-control.service';
import { AlertNotificationService } from 'src/app/services/alert-notification.service';
import { CommonService } from 'src/app/services/common.service';
import { MainLayoutService } from 'src/app/services/main-layout.service';
import {
  ProjectFinanceAuthorizationAction,
  ProjectFinanceAuthorizationHistory,
  ProjectFinanceManualAuthorizationAction
} from 'src/app/models/entities/project-finance';

const LOCK_ACTIONS: ProjectFinanceAuthorizationAction[] = ['AUTO_LOCK', 'MANUAL_LOCK'];

@Component({
  selector: 'projects-finance-authorization',
  imports: [NgbTooltipModule, TextAreaFieldComponent],
  templateUrl: './projects-finance-authorization.component.html',
  styleUrl: './projects-finance-authorization.component.scss'
})
export class ProjectsFinanceAuthorizationComponent implements OnInit, OnChanges, OnDestroy {
  @Input() projectId?: string | number;
  @Input() showActions: boolean = false;

  @ViewChild('overrideModal') private overrideModal!: TemplateRef<unknown>;

  currentEntry: ProjectFinanceAuthorizationHistory | null = null;
  readonly reasonControl = new FormControl<string>('', { nonNullable: true, validators: [Validators.required] });
  isLoaded = false;
  canModify = false;
  isSaving = false;

  private modalRef?: NgbModalRef;
  private destroy$ = new Subject<void>();

  constructor(
    private projectFinanceService: ProjectFinanceService,
    private accessControlService: AccessControlService,
    private alertNotificationService: AlertNotificationService,
    private modalService: NgbModal,
    private mainLayoutService: MainLayoutService,
    private commonService: CommonService
  ) { }

  ngOnInit(): void {
    this.mainLayoutService.activeProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe(profileCode => {
        this.canModify = this.accessControlService.hasFeatureActionAccess(profileCode, 'PROJECTS_FINANCE', 'modify');
      });

    this.projectFinanceService.authorizationChanged$
      .pipe(
        filter(projectId => projectId === Number(this.projectId)),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.loadHistory());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectId'] && this.projectId) {
      this.loadHistory();
    }
  }

  ngOnDestroy(): void {
    this.modalRef?.dismiss();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isBlocked(): boolean {
    return !!this.currentEntry && LOCK_ACTIONS.includes(this.currentEntry.action);
  }

  get badgeLabel(): string {
    switch (this.currentEntry?.action) {
      case 'AUTO_LOCK':
        return 'Finance Blocked — Overdue';
      case 'MANUAL_LOCK':
        return 'Finance Blocked — Manual lock';
      case 'MANUAL_UNLOCK':
        return 'Finance Authorized — Manual unlock';
      default:
        return 'Finance Authorized';
    }
  }

  get tooltipText(): string {
    const entry = this.currentEntry;
    if (!entry) {
      return 'All instalments on schedule. Only the 2nd instalment onward triggers the automatic block.';
    }
    const reason = entry.reason ? ` Reason: ${entry.reason}` : '';

    const when = this.commonService.formatDateValue(entry.createTime, true);
    const who = entry.user ? `${entry.user.firstName} ${entry.user.lastName}`.trim() : 'an authorized user';
    switch (entry.action) {
      case 'AUTO_LOCK':
        return `Automatically blocked on ${when} — an instalment from the 2nd onward is overdue. Authorized Finance roles can unlock manually.${reason}`;
      case 'MANUAL_LOCK':
        return `Manually locked by ${who} on ${when}.${reason}`;
      default:
        return `Manually unlocked by ${who} on ${when}.${reason}`;
    }
  }

  get showOverrideButton(): boolean {
    return this.showActions && this.canModify && this.isLoaded;
  }

  get overrideAction(): ProjectFinanceManualAuthorizationAction {
    return this.isBlocked ? 'MANUAL_UNLOCK' : 'MANUAL_LOCK';
  }

  get overrideMessage(): string {
    return this.overrideAction === 'MANUAL_UNLOCK'
      ? 'The project will be authorized by Finance even though it is blocked.'
      : 'Work on the project will no longer be authorized by Finance.';
  }

  openOverrideModal(): void {
    if (this.modalRef) return;
    this.reasonControl.reset('');
    this.modalRef = this.modalService.open(this.overrideModal, { centered: true, backdrop: 'static' });
    this.modalRef.result.finally(() => this.modalRef = undefined).catch(() => undefined);
  }

  closeOverrideModal(): void {
    this.modalRef?.dismiss();
  }

  confirmOverride(): void {
    const reason = this.reasonControl.value.trim();
    if (!reason) {
      this.reasonControl.setValue('');
      this.reasonControl.markAsTouched();
      return;
    }
    if (!this.projectId) return;

    const action = this.overrideAction;
    this.isSaving = true;
    this.projectFinanceService.overrideAuthorization(Number(this.projectId), { action, reason }).subscribe({
      next: () => {
        this.isSaving = false;
        this.modalRef?.close();
        this.alertNotificationService.success(action === 'MANUAL_UNLOCK' ? 'Finance authorization unlocked' : 'Finance authorization locked');
      },
      error: (error) => {
        this.isSaving = false;
        console.error('Error updating finance authorization:', error);
        this.alertNotificationService.error(this.projectFinanceService.errorMessage(error, 'Error updating finance authorization'));
      }
    });
  }

  private loadHistory(): void {
    if (!this.projectId) return;
    this.projectFinanceService.listAuthorizationHistory(Number(this.projectId)).subscribe({
      next: (response) => {
        const history = this.projectFinanceService.readResponseKey<ProjectFinanceAuthorizationHistory[]>(
          response, 'projectFinanceAuthorizationHistories'
        ) ?? [];
        this.currentEntry = history[0] ?? null;
        this.isLoaded = true;
      },
      error: (error) => {
        console.error('Error loading finance authorization history:', error);
        this.currentEntry = null;
        this.isLoaded = false;
      }
    });
  }
}
